/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { RequestRmaNotesContext } from "@eisinc/App/Contexts/RequestRmaNotesContext";
import TextArea, { TextAreaProps } from "@eisinc/MobiusOverrides/TextArea";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import setCanSendReturnRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetCanSendReturnRequest";
import setReturnNotes from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetReturnNotes";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    returnNotes: state.pages.requestRma.returnNotes,
    orderLines: state.pages.requestRma.orderLines,
});

const mapDispatchToProps = {
    setReturnNotes,
    setCanSendReturnRequest,
};

export interface RequestRmaNotesStyles {
    wrapper?: InjectableCss;
    notesTextArea?: TextAreaProps;
}

export const notesStyles: RequestRmaNotesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 20px;
        `,
    },
};

const styles = notesStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RequestRmaNotes: FC<Props> = ({ returnNotes, setReturnNotes, orderLines, setCanSendReturnRequest }) => {
    const requestRmaNotesContext = useContext(RequestRmaNotesContext);
    const { value: order } = useContext(OrderStateContext);

    React.useEffect(() => {
        setCanSendReturnRequest({
            value:
                orderLines.some(line => line.rmaQtyRequested > 0 && line.returnReason) &&
                !orderLines.some(
                    line => line.rmaQtyRequested > line.qtyOrdered || (line.rmaQtyRequested > 0 && !line.returnReason),
                ),
        });

        if (requestRmaNotesContext?.isSubmitted) {
            requestRmaNotesContext.setRequestRmaNotesError(!(returnNotes && returnNotes.trim()));
        }
    }, [returnNotes]);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReturnNotes({ returnNotes: event.currentTarget.value });
    };

    if (!order) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextArea
                label={translate("Return Notes*")}
                value={returnNotes}
                rows={5}
                placeholder={translate("Enter your notes here")}
                onChange={changeHandler}
                error={requestRmaNotesContext?.requestRmaNotesError && siteMessage("Custom_Request_Rma_notes_Error")}
                {...styles.notesTextArea}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaNotes),
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        displayName: "Notes",
        fieldDefinitions: [],
    },
};

export default widgetModule;
